import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CompanyTaxTypeApiService } from '../../empresas/services/company-tax-type-api.service';
import { CompanyTaxType } from '../../empresas/models/empresa.model';
import { CurrencyService } from '../../currency/currency.service';
import { CountryResponse } from '../../currency/models/currency.model';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-company-tax-type-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './company-tax-type-form.component.html',
})
export class CompanyTaxTypeFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form model
  name = '';
  private originalName = '';
  countryId: number | null = null;
  formatRegex = '';

  countries: CountryResponse[] = [];

  readonly NAME_MAX = 50;

  constructor(
    private api: CompanyTaxTypeApiService,
    private currencyService: CurrencyService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarPaises();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
  }

  private cargarPaises(): void {
    this.currencyService.getCountries().subscribe({
      next: (countries) => { this.countries = countries; },
      error: () => {},
    });
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';
    this.api.getById(id).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.countryId = item.countryId ?? null;
        this.formatRegex = item.formatRegex || '';
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el tipo de documento';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Tipo de Documento' : 'Nuevo Tipo de Documento';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    // businessId NUNCA se envía: el backend lo toma del JWT.
    const request: Omit<CompanyTaxType, 'id'> = {
      name: this.name.trim(),
      countryId: this.countryId ?? undefined,
      formatRegex: this.formatRegex.trim() || undefined,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Tipo de documento',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/tipos-documento']);
        },
        error: (err) => {
          this.saving = false;
          const msg = this.errorMessage(err, 'Error al actualizar el tipo de documento');
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Tipo de documento', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/tipos-documento']);
        },
        error: (err) => {
          this.saving = false;
          const msg = this.errorMessage(err, 'Error al crear el tipo de documento');
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        },
      });
    }
  }

  private errorMessage(err: { status?: number; error?: { message?: string } }, fallback: string): string {
    if (err.status === 403) return 'Necesitas rol Manager para crear o editar tipos de documento';
    if (err.status === 409) return 'Ya existe un tipo de documento con ese nombre';
    return err.error?.message || fallback;
  }

  cancelar(): void {
    this.router.navigate(['/config-empresas/tipos-documento']);
  }
}
